import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { urlForReactPdfFetch } from "./cmsPdfProxyUrl";
import { getPdfViewerStyles } from "./docsViewerStyles";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Icon, Button, Text, Box } from "@repo/ui";
import { useTranslation } from "react-i18next";
import { useTheme } from "@repo/tokens";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

type PdfFile = string | File | ArrayBuffer;

interface PdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  files: PdfFile[];
  initialPage?: number;
  initialScale?: number;
  minScale?: number;
  maxScale?: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const DocsViewer = ({
  isOpen,
  onClose,
  files,
  initialPage = 1,
  initialScale = 1,
  minScale = 0.6,
  maxScale = 2.2,
}: PdfViewerModalProps) => {
  const validFiles = files.filter((value) => value != null);
  const filesSignature = useMemo(
    () =>
      validFiles
        .map((value) => {
          if (typeof value === "string") return value;
          if (value instanceof File) return `${value.name}:${value.size}`;
          if (value instanceof ArrayBuffer) return `buffer:${value.byteLength}`;
          return "file";
        })
        .join("|"),
    [validFiles],
  );
  const filesForReactPdf = useMemo(
    () =>
      validFiles.map((f) =>
        typeof f === "string" ? urlForReactPdfFetch(f) : f,
      ),
    [validFiles],
  );
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageInput, setPageInput] = useState(String(initialPage));
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [loaderProgress, setLoaderProgress] = useState(2);
  const [hasPdfError, setHasPdfError] = useState(false);
  const [pdfErrorMessage, setPdfErrorMessage] = useState("");
  const [scale, setScale] = useState(initialScale);
  const [pageWidth, setPageWidth] = useState(780);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1024 : false,
  );

  const { t } = useTranslation("common");
  const { theme } = useTheme();

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const forcedPageRef = useRef<number | null>(null);
  const forcedPageUntilRef = useRef(0);
  const activeFile = validFiles[activeFileIndex] ?? null;
  const activeFileForDocument = filesForReactPdf[activeFileIndex] ?? null;
  const activeFileUrl = typeof activeFile === "string" ? activeFile : null;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const updateWidth = () => {
      const width = viewportRef.current?.clientWidth ?? 0;
      setPageWidth(Math.max(300, width - 40));
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    if (viewportRef.current) {
      observer.observe(viewportRef.current);
    }

    return () => observer.disconnect();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveFileIndex(0);
  }, [isOpen, filesSignature]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentPage(initialPage);
    setPageInput(String(initialPage));
    setIsPdfLoading(true);
    setLoaderProgress(2);
    setHasPdfError(false);
    setPdfErrorMessage("");
    setTotalPages(0);
    renderedPagesRef.current.clear();
    setScale(initialScale);
  }, [activeFileForDocument, initialPage, initialScale, isOpen]);

  useEffect(() => {
    if (!isOpen || totalPages <= 0) return;
    setCurrentPage((prev) => clamp(prev, 1, totalPages));
  }, [isOpen, totalPages]);

  const ZoomOut = () =>
    setScale((prev) => clamp(prev - 0.1, minScale, maxScale));
  const ZoomIn = () =>
    setScale((prev) => clamp(prev + 0.1, minScale, maxScale));

  const syncCurrentPageFromScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || totalPages <= 0 || isPdfLoading) return;

    const viewportRect = viewport.getBoundingClientRect();
    const viewportCenter = viewportRect.top + viewportRect.height / 2;
    let closestPage = currentPage;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let page = 1; page <= totalPages; page += 1) {
      const pageNode = pageRefs.current[page];
      if (!pageNode) continue;

      const pageRect = pageNode.getBoundingClientRect();
      if (pageRect.width <= 0 || pageRect.height <= 0) continue;

      const pageCenter = pageRect.top + pageRect.height / 2;
      const distance = Math.abs(pageCenter - viewportCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = page;
      }
    }

    setCurrentPage((prev) => {
      if (prev === closestPage) return prev;
      setPageInput(String(closestPage));
      return closestPage;
    });
  }, [currentPage, isPdfLoading, totalPages]);

  useEffect(() => {
    if (!isOpen || totalPages <= 0 || isPdfLoading) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    let animationFrameId = 0;
    const handleScroll = () => {
      if (animationFrameId) return;
      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = 0;
        syncCurrentPageFromScroll();
      });
    };

    viewport.addEventListener("scroll", handleScroll, { passive: true });
    syncCurrentPageFromScroll();

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOpen, isPdfLoading, syncCurrentPageFromScroll, totalPages]);

  const scrollToPage = (page: number) => {
    const targetRef = pageRefs.current[page];
    if (targetRef) {
      targetRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goToPage = (targetPage: number) => {
    if (totalPages <= 0) return;
    if (!Number.isFinite(targetPage)) return;

    const nextPage = Math.round(targetPage);
    if (nextPage <= 0 || nextPage > totalPages) return;

    forcedPageRef.current = nextPage;
    forcedPageUntilRef.current = Date.now() + 1200;
    setCurrentPage(nextPage);
    setPageInput(String(nextPage));
    scrollToPage(nextPage);
  };

  const goToPdf = (nextIndex: number) => {
    if (!Number.isInteger(nextIndex)) return;
    if (nextIndex < 0 || nextIndex >= validFiles.length) return;
    setActiveFileIndex(nextIndex);
  };

  if (!isOpen) return null;
  const styles = getPdfViewerStyles(theme, isMobile);

  return (
    <div style={styles.overlay} onClick={onClose} role="presentation">
      <Box
        style={styles.modal}
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        {isPdfLoading && (
          <div style={styles.topLoaderTrack}>
            <div
              style={{
                ...styles.topLoaderBar,
                width: `${loaderProgress}%`,
              }}
            />
          </div>
        )}
        <div style={styles.toolbar}>
          <div style={styles.toolbarTop}>
            <div style={styles.toolbarTopSpacer} />
            {validFiles.length > 1 ? (
              <div style={styles.fileControls}>
                <Button
                  style={{
                    ...styles.iconButton,
                    ...(activeFileIndex <= 0 ? styles.disabledIconButton : {}),
                  }}
                  variant="ghost"
                  onClick={() => goToPdf(activeFileIndex - 1)}
                  disabled={activeFileIndex <= 0}
                  aria-label="Previous PDF"
                >
                  <Icon name="arrowBack" size={16} />
                </Button>
                <Text style={styles.fileText}>
                  PDF {validFiles.length > 0 ? activeFileIndex + 1 : 0}/
                  {validFiles.length}
                </Text>
                <Button
                  style={{
                    ...styles.iconButton,
                    ...(activeFileIndex >= validFiles.length - 1
                      ? styles.disabledIconButton
                      : {}),
                  }}
                  variant="ghost"
                  onClick={() => goToPdf(activeFileIndex + 1)}
                  disabled={activeFileIndex >= validFiles.length - 1}
                  aria-label="Next PDF"
                >
                  <span
                    style={{
                      display: "inline-flex",
                      transform: "rotate(180deg)",
                    }}
                  >
                    <Icon name="arrowBack" size={16} />
                  </span>
                </Button>
              </div>
            ) : null}
            <Button
              style={styles.toolbarCloseButton}
              variant="ghost"
              onClick={onClose}
              type="button"
              aria-label="Close PDF viewer"
            >
              <Icon name="close" size={26} />
            </Button>
          </div>
          <div style={styles.toolbarBottom}>
            <div style={styles.centerControls}>
              <div style={styles.pageControls}>
                <div style={styles.pageText}>
                  <Text>{t("page")}</Text>
                  <input
                    style={styles.inlinePageInput}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={isPdfLoading ? "0" : pageInput}
                    onChange={(event) =>
                      setPageInput(event.target.value.replace(/[^0-9]/g, ""))
                    }
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      const nextPage = Number(pageInput);
                      if (
                        !Number.isFinite(nextPage) ||
                        nextPage <= 0 ||
                        nextPage > totalPages
                      ) {
                        setPageInput(String(currentPage));
                        return;
                      }
                      goToPage(nextPage);
                    }}
                    onBlur={() => {
                      const nextPage = Number(pageInput);
                      if (
                        !Number.isFinite(nextPage) ||
                        nextPage <= 0 ||
                        nextPage > totalPages
                      ) {
                        setPageInput(String(currentPage));
                        return;
                      }
                      goToPage(nextPage);
                    }}
                    aria-label="Enter page number"
                  />
                  <Text>/{isPdfLoading ? 0 : totalPages || 0}</Text>
                </div>
              </div>
              <div style={styles.zoomControls}>
                <Button
                  style={{
                    ...styles.iconButton,
                    ...(scale <= minScale ? styles.disabledIconButton : {}),
                  }}
                  variant="ghost"
                  onClick={ZoomOut}
                  disabled={scale <= minScale}
                  aria-label="Zoom out"
                >
                  <Icon name="zoomOut" size={26} />
                </Button>

                <Button
                  style={{
                    ...styles.iconButton,
                    ...(scale >= maxScale ? styles.disabledIconButton : {}),
                  }}
                  variant="ghost"
                  type="button"
                  onClick={ZoomIn}
                  disabled={scale >= maxScale}
                  aria-label="Zoom in"
                >
                  <Icon name="zoomIn" size={26} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.viewerSurface} ref={viewportRef}>
          {hasPdfError && (
            <div style={styles.errorOverlay}>
              <div>
                <div>
                  {activeFileUrl
                    ? "This PDF can't be previewed here."
                    : "No PDF available."}
                </div>
                {pdfErrorMessage ? (
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    {pdfErrorMessage}
                  </div>
                ) : null}
              </div>
            </div>
          )}
          <Document
            file={activeFileForDocument}
            loading={null}
            noData={<div style={styles.status}>No PDF available.</div>}
            error={null}
            onLoadSuccess={({ numPages }) => {
              setTotalPages(numPages);
              forcedPageRef.current = 1;
              forcedPageUntilRef.current = Date.now() + 1200;
              setCurrentPage(1);
              setPageInput("1");
              setTimeout(() => {
                scrollToPage(1);
              }, 0);
              setIsPdfLoading(true);
              setLoaderProgress(8);
              setHasPdfError(false);
              setPdfErrorMessage("");
              renderedPagesRef.current.clear();
            }}
            onLoadError={(error) => {
              setLoaderProgress(100);
              setIsPdfLoading(false);
              setHasPdfError(true);
              setPdfErrorMessage(
                error instanceof Error ? error.message : "Failed to load PDF.",
              );
            }}
            onSourceError={(error) => {
              setLoaderProgress(100);
              setIsPdfLoading(false);
              setHasPdfError(true);
              setPdfErrorMessage(
                error instanceof Error ? error.message : "Failed to fetch PDF.",
              );
            }}
          >
            <div
              style={{
                ...styles.pageStack,
                opacity: isPdfLoading ? 0 : 1,
                transition: "opacity 0.2s ease",
              }}
            >
              {Array.from({ length: totalPages }, (_, index) => (
                <div
                  style={styles.pageWrap}
                  key={`page-${index + 1}`}
                  ref={(node) => {
                    pageRefs.current[index + 1] = node;
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    scale={scale}
                    renderTextLayer
                    renderAnnotationLayer
                    loading={null}
                    onRenderError={() => {
                      setLoaderProgress(100);
                      setIsPdfLoading(false);
                      setHasPdfError(true);
                    }}
                    onRenderSuccess={() => {
                      renderedPagesRef.current.add(index + 1);
                      const renderedCount = renderedPagesRef.current.size;
                      if (totalPages > 0) {
                        const progress = Math.min(
                          99,
                          8 + (renderedCount / totalPages) * 91,
                        );
                        setLoaderProgress(progress);
                      }
                      if (renderedCount >= totalPages) {
                        setLoaderProgress(100);
                        setIsPdfLoading(false);
                      }
                      const forcedPage = forcedPageRef.current;
                      const isForceWindowActive =
                        Date.now() <= forcedPageUntilRef.current;
                      if (
                        isForceWindowActive &&
                        forcedPage != null &&
                        index + 1 === forcedPage
                      ) {
                        setCurrentPage(forcedPage);
                        setPageInput(String(forcedPage));
                        forcedPageRef.current = null;
                        forcedPageUntilRef.current = 0;
                      }
                      if (index + 1 === currentPage) {
                        setPageInput(String(currentPage));
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </Document>
        </div>
      </Box>
    </div>
  );
};
